/**
 * Document tax helpers shared by Customers / Sales / Finance / Settings UI.
 * Payroll and tax filing stay in external systems.
 */

export const TAX_ID_TYPE_OPTIONS = [
  { value: 'ein', label: 'EIN (US)', countries: ['US'] },
  { value: 'ssn', label: 'SSN (US payee)', countries: ['US'] },
  { value: 'itin', label: 'ITIN (US)', countries: ['US'] },
  { value: 'bn_gst', label: 'BN / GST/HST # (CA)', countries: ['CA'] },
  { value: 'sin', label: 'SIN (CA payee)', countries: ['CA'] },
  { value: 'gstin', label: 'GSTIN (IN)', countries: ['IN'] },
  { value: 'pan', label: 'PAN (IN)', countries: ['IN'] },
  { value: 'other', label: 'Other tax ID', countries: ['US', 'CA', 'IN'] },
];

export const TAX_REGIME_OPTIONS = [
  { value: 'none', label: 'No tax / handled externally' },
  { value: 'us_sales', label: 'United States sales tax' },
  { value: 'ca_gst', label: 'Canada GST/HST/PST' },
  { value: 'in_gst', label: 'India GST (CGST/SGST/IGST)' },
  { value: 'manual', label: 'Manual flat rate' },
];

export const GST_TREATMENT_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'reverse_charge', label: 'Reverse charge' },
  { value: 'export', label: 'Export' },
  { value: 'sez', label: 'SEZ' },
];

export function defaultTaxIdTypeForCountry(countryCode) {
  const cc = String(countryCode || '').toUpperCase();
  if (cc === 'US') return 'ein';
  if (cc === 'CA') return 'bn_gst';
  if (cc === 'IN') return 'gstin';
  return 'other';
}

export function taxIdLabel(type) {
  return TAX_ID_TYPE_OPTIONS.find((o) => o.value === type)?.label || 'Tax ID';
}

export function defaultRegimeForCountry(countryCode) {
  const cc = String(countryCode || '').toUpperCase();
  if (cc === 'US') return 'us_sales';
  if (cc === 'CA') return 'ca_gst';
  if (cc === 'IN') return 'in_gst';
  return 'manual';
}

/**
 * Seller profile for a quotation or invoice.
 * Country wins, then currency (INR → India GST), then the default.
 * Keep in step with selectOrgTaxProfile in orgTaxProfileDb.js.
 */
export function selectOrgTaxProfile(profiles, defaultId, { countryCode, currency } = {}) {
  const list = Array.isArray(profiles) ? profiles : [];
  if (!list.length) return null;
  const country = String(countryCode || '')
    .trim()
    .toUpperCase();
  if (country) {
    const matches = list.filter((p) => String(p.country_code || '').toUpperCase() === country);
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) {
      return matches.find((p) => String(p.id) === String(defaultId)) || matches[0];
    }
  }
  const curr = String(currency || '')
    .trim()
    .toUpperCase();
  const hint =
    curr === 'INR'
      ? { country: 'IN', regime: 'in_gst' }
      : curr === 'CAD'
        ? { country: 'CA', regime: 'ca_gst' }
        : curr === 'USD'
          ? { country: 'US', regime: 'us_sales' }
          : null;
  if (hint) {
    const hit =
      list.find((p) => String(p.country_code || '').toUpperCase() === hint.country) ||
      list.find((p) => p.default_tax_regime === hint.regime);
    if (hit) return hit;
  }
  return list.find((p) => String(p.id) === String(defaultId)) || list[0];
}
